import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  PartnerDealBriefingSectionResponse,
  PartnerDealBriefingVersionResponse,
} from '@interfaces/partners/responses/partner-deal-briefing.response';
import { SharedModule } from '@src/app/shared/shared.module';
import { VaultDateTimePipe } from '@src/app/shared/pipes/vault-date-time.pipe';
import {
  versionMenuModel,
  versionTag,
} from '@src/app/features/dashboard/commercial/pages/deal-detail/components/deal-briefing-tab/briefing-presenter';

/**
 * Menu "Versão N" do visualizador do Briefing (card 3878, Q11 opção B). O título da versão aberta é o botão; o menu
 * lista as versões geradas (e a que está gerando) com Ver e Tornar atual e, numa seção curta, as que não foram
 * geradas com o motivo numa linha. Ver não troca a atual: só "Tornar atual" troca (é a versão que o chat lê e que vai
 * ao banco).
 */
@Component({
  selector: 'app-briefing-version-menu',
  standalone: true,
  imports: [SharedModule, VaultDateTimePipe],
  templateUrl: './briefing-version-menu.component.html',
  styleUrl: './briefing-version-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingVersionMenuComponent {
  private readonly host = inject(ElementRef<HTMLElement>);

  /** Versões já ordenadas da mais nova para a mais antiga. */
  readonly versions = input<readonly PartnerDealBriefingVersionResponse[]>([]);
  readonly catalog = input<readonly PartnerDealBriefingSectionResponse[]>([]);
  /** Versão aberta no visualizador (o título do botão). */
  readonly viewed = input.required<PartnerDealBriefingVersionResponse>();
  readonly settingCurrentId = input<string | null>(null);
  /** Dica do selo quando a versão aberta está gerando ("Pedido por fulano em hora..."). */
  readonly phaseHint = input<string | null>(null);
  /** `PARTNERDEALBRIEFING_SET_CURRENT`: sem ela, o menu só tem Ver (Tornar atual some). */
  readonly canSetCurrent = input(true);

  readonly versionSelected = output<string>();
  readonly makeCurrent = output<string>();

  protected readonly open = signal(false);

  /** Selo do botão: fase na que gera, "atual", "não gerada" na que falhou. */
  protected readonly viewedTag = computed(() => {
    const viewed = this.viewed();
    if (viewed.status === 'Failed') return { label: 'não gerada', tone: 'danger' as const };
    return versionTag(viewed);
  });

  /** Geradas, não geradas e contagem (`versionMenuModel`, testado no spec do presenter). */
  protected readonly model = computed(() => versionMenuModel(this.versions(), this.catalog()));

  protected toggle(): void {
    this.open.update(value => !value);
  }

  protected view(id: string): void {
    this.versionSelected.emit(id);
    this.open.set(false);
  }

  protected setCurrent(id: string): void {
    this.makeCurrent.emit(id);
    this.open.set(false);
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) {
      this.open.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.open.set(false);
  }
}
